import { useRef, useEffect, useState } from "preact/hooks";
import { useReviews, type ReviewFilter } from "@/hooks/useReviews";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { cn } from "@/lib/utils";
import {
  generateUUID,
  trackEvent,
  type ModalOpenSource,
} from "@/utils/tracking";
import { VerifiedFooter } from "./VerifiedFooter";
import { Sheet, SheetContent, SheetFooter, SheetHeader } from "./ui/sheet";
import { FilterChips } from "./ui/filter-chips";
import type { ProductTab } from "@/utils/metadata";
import { ReviewItem } from "./ReviewItem";
import StarRating from "./StarRating";

// Temporarily hidden: the "Featured reviews verified by Social Ocean" footer at
// the bottom of the reviews sheet. Flip back to true to restore it.
const SHOW_VERIFIED_FOOTER = false;

function ReviewSkeleton() {
  return (
    <div className="tw:space-y-2 tw:border-b tw:border-[#c5cad2] tw:py-4 tw:animate-pulse">
      <div className="tw:h-4 tw:w-28 tw:rounded tw:bg-gray-200" />
      <div className="tw:h-3 tw:w-20 tw:rounded tw:bg-gray-200" />
      <div className="tw:h-3 tw:w-full tw:rounded tw:bg-gray-100" />
      <div className="tw:h-3 tw:w-5/6 tw:rounded tw:bg-gray-100" />
    </div>
  );
}

interface ReviewsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  source: ModalOpenSource;
  /** Active chip key (lifted to the carousel so its chips can pre-set it). */
  topic: string | null;
  onTopicChange: (topic: string | null) => void;
  /** Chips from the unfiltered query — stable while a filtered page loads. */
  fallbackFilters: ReviewFilter[];
  /** The carousel's product tab, when it has them; null for the page's own. */
  productId?: number | null;
  /** That tab's name, so the sheet says whose reviews it is listing. */
  productLabel?: string | null;
  /** The carousel's product tabs; when present the sheet shows them too. */
  productTabs?: ProductTab[];
  /** Switches the shared product tab (carousel and sheet stay in step). */
  onProductChange?: (productId: number) => void;
}

export function ReviewsSheet({
  isOpen,
  onOpenChange,
  source,
  topic,
  onTopicChange,
  fallbackFilters,
  productId = null,
  productLabel = null,
  productTabs = [],
  onProductChange,
}: ReviewsSheetProps) {
  // On a bundle page (Trial Pack, rituals) the sheet's row is the products,
  // as under the carousel, rather than topic chips under one product's name:
  // "Show all" from Nectar's tab otherwise left no way to read the Mist's.
  const hasProductTabs = productTabs.length > 0;
  // The sheet owns its data: unfiltered (topic null) shares the carousel's
  // query cache entry; a topic gets its own paginated query.
  const {
    data,
    isFetching,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useReviews(topic, productId);

  // During a filter switch the query serves the PREVIOUS topic's list as
  // placeholder — showing those (wrong) reviews for a beat is confusing, so
  // render skeletons until the filtered page arrives.
  const showSkeletons = topic !== null && isPlaceholderData;

  const reviews = data?.reviews ?? [];
  const total = data?.total ?? 0;
  const filters = data?.filters?.length ? data.filters : fallbackFilters;
  const [showFallbackNotice, setShowFallbackNotice] = useState(false);

  // Stale-count safety: a chip can promise reviews the filtered query no
  // longer finds (10-min server cache). Reset to All instead of an empty sheet.
  useEffect(() => {
    if (!isOpen || !topic || isFetching) return;
    if (data && data.reviews.length === 0) {
      onTopicChange(null);
      setShowFallbackNotice(true);
    }
  }, [isOpen, topic, isFetching, data]);

  useEffect(() => {
    if (topic) setShowFallbackNotice(false);
  }, [topic]);

  useEffect(() => {
    if (!isOpen) setShowFallbackNotice(false);
  }, [isOpen]);

  const shareEventIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined = undefined;
    let startTime = Date.now();

    if (isOpen) {
      startTime = Date.now();

      shareEventIdRef.current = generateUUID();

      // Track open event
      trackEvent("reviews.modal_opened", {
        clientSideId: shareEventIdRef.current,
        source,
      });

      // Send updates every 5 seconds
      interval = setInterval(() => {
        const duration = Math.floor((Date.now() - startTime) / 1000);

        if (shareEventIdRef.current) {
          trackEvent("reviews.modal_opened", {
            duration,
            clientSideId: shareEventIdRef.current,
            source,
          });
        }
      }, 5000);
    }

    return () => {
      if (interval !== undefined) {
        // Send final duration on unmount/close
        const duration = Math.floor((Date.now() - startTime) / 1000);
        if (shareEventIdRef.current) {
          trackEvent("reviews.modal_opened", {
            duration,
            clientSideId: shareEventIdRef.current,
            source,
          });
          // Reset ID after sending final update
          shareEventIdRef.current = undefined;
        }

        clearInterval(interval);
      }
    };
  }, [isOpen]);

  // Lift the widget host above other on-page widgets (e.g. the buy-again
  // floating panel) while the full-screen sheet is open, then restore it.
  useEffect(() => {
    if (!isOpen) return;
    // Keep in sync with WIDGET_HOST_ID in src/widget/index.tsx
    const host = document.getElementById("social-ocean-review-host");
    if (!host) return;
    const previousZIndex = host.style.zIndex;
    host.style.zIndex = "2147483646";
    return () => {
      host.style.zIndex = previousZIndex;
    };
  }, [isOpen]);

  // base-ui moves focus into the sheet when it opens; with no explicit target
  // it focuses the first tabbable element — the "All" filter chip — lighting up
  // that chip's focus-visible ring every time the sheet expands. Send initial
  // focus to the heading instead (focusable via tabIndex=-1 but kept out of the
  // Tab order) so opening the sheet highlights nothing; Tab still rings the chips.
  const titleRef = useRef<HTMLHeadingElement>(null);

  // Infinite scroll: observe a sentinel at the bottom of the scroll container
  // (used as the observer root) and load the next page as it comes into view.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // `!isFetchNextPageError` matters: the sentinel sits inside the root margin
  // once the reader is at the bottom, so without it a failing page would be
  // re-requested the instant the spinner clears, in a tight loop. On failure we
  // stand down and offer a manual retry instead.
  const sentinelRef = useIntersectionObserver({
    enabled:
      isOpen && hasNextPage && !isFetchingNextPage && !isFetchNextPageError,
    rootRef: scrollRef,
    rootMargin: "300px",
    onIntersect: () => {
      if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
        fetchNextPage();
      }
    },
  });

  // Scroll-direction chrome: reading down hides the ratings row, filter chips
  // and footer for more room (especially on small screens); scrolling up
  // brings them back. Only the title stays put.
  //
  // Collapsing the chrome RESIZES the scroll container, which makes the
  // browser clamp scrollTop and emit synthetic scroll events — an unmitigated
  // handler reads those as direction flips and oscillates (worst right at the
  // bottom, where it also starves the infinite-scroll sentinel). Two guards:
  // ignore events during the collapse transition, and never toggle from
  // events near the bottom edge.
  const [chromeHidden, setChromeHidden] = useState(false);
  const lastScrollTop = useRef(0);
  const ignoreScrollUntil = useRef(0);
  const onListScroll = (event: { currentTarget: HTMLDivElement }) => {
    const el = event.currentTarget;
    const top = el.scrollTop;
    const delta = top - lastScrollTop.current;
    lastScrollTop.current = top;
    if (performance.now() < ignoreScrollUntil.current) return;
    if (Math.abs(delta) < 8) return; // ignore sub-threshold jitter
    const nearBottom =
      el.scrollHeight > el.clientHeight &&
      top + el.clientHeight >= el.scrollHeight - 120;

    if (delta > 0 && top > 60 && !chromeHidden) {
      setChromeHidden(true);
      ignoreScrollUntil.current = performance.now() + 250; // transition window
    } else if (delta < 0 && !nearBottom && chromeHidden) {
      setChromeHidden(false);
      ignoreScrollUntil.current = performance.now() + 250;
    }
  };

  useEffect(() => {
    if (isOpen) return;
    setChromeHidden(false);
    lastScrollTop.current = 0;
    ignoreScrollUntil.current = 0;
  }, [isOpen]);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        onOpenChange={onOpenChange}
        side="bottom"
        initialFocus={() => titleRef.current}
        className={cn(
          "tw:rounded-t-4xl tw:p-0 tw:flex tw:flex-col tw:min-h-[calc(100dvh-1rem)] tw:max-h-[calc(100dvh-1rem)] tw:gap-0 tw:transition-all tw:duration-300 tw:md:w-[calc(100%-1rem)] tw:md:max-w-lg tw:ms-auto tw:md:me-2",
        )}
      >
        <SheetHeader className="tw:px-4 tw:pb-0 tw:rounded-t-3xl tw:items-start tw:text-left">
          <h2
            ref={titleRef}
            tabIndex={-1}
            className="tw:text-lg tw:font-medium tw:leading-none tw:tracking-tight tw:text-[#523C33] tw:outline-none"
          >
            From Loyal Customers
          </h2>
          {productLabel && !hasProductTabs ? (
            <p className="tw:text-sm tw:font-medium tw:text-[#523C33]">
              {productLabel}
            </p>
          ) : null}
          <div
            data-testid="sheet-ratings-chrome"
            aria-hidden={chromeHidden}
            className={cn(
              "tw:flex tw:items-center tw:justify-start tw:gap-1.5 tw:overflow-hidden tw:transition-all tw:duration-200",
              chromeHidden
                ? "tw:max-h-0 tw:opacity-0"
                : "tw:max-h-10 tw:opacity-100",
            )}
          >
            <StarRating rating={5} size="sm" />
            <span className="tw:text-sm tw:font-medium">
              {total.toLocaleString()}{" "}
            </span>
            <span className="tw:text-sm tw:font-medium tw:text-muted-foreground">
              ratings
            </span>
          </div>
          <div
            aria-hidden={chromeHidden}
            className={cn(
              "tw:w-full tw:overflow-hidden tw:transition-all tw:duration-200",
              chromeHidden
                ? "tw:max-h-0 tw:opacity-0"
                : "tw:max-h-20 tw:opacity-100",
            )}
          >
            {hasProductTabs ? (
              <FilterChips
                ariaLabel="Show reviews for"
                filters={productTabs.map((t) => ({
                  key: String(t.id),
                  label: t.label,
                  emoji: "",
                  count: 0,
                }))}
                active={productId != null ? String(productId) : null}
                onSelect={(key) => {
                  // A tab row always has one selected; re-tapping it is a no-op.
                  if (!key) return;
                  trackEvent("reviews.filter_chip_tapped", {
                    chip: `product:${key}`,
                    surface: "sheet",
                  });
                  onProductChange?.(Number(key));
                }}
              />
            ) : (
              <FilterChips
                filters={filters}
                active={topic}
                showAll
                onSelect={(key) => {
                  if (key)
                    trackEvent("reviews.filter_chip_tapped", {
                      chip: key,
                      surface: "sheet",
                    });
                  onTopicChange(key);
                }}
              />
            )}
          </div>
          {showFallbackNotice ? (
            <p className="tw:text-xs tw:text-gray-500">Showing all reviews</p>
          ) : null}
        </SheetHeader>

        <div
          ref={scrollRef}
          data-testid="reviews-scroll"
          onScroll={onListScroll}
          className={cn(
            "tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden tw:px-4 tw:pb-2 tw:gap-2.3 tw:bg-white",
            isFetching &&
              !isFetchingNextPage &&
              "tw:opacity-60 tw:transition-opacity",
          )}
        >
          {showSkeletons ? (
            <div data-testid="reviews-skeleton" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <ReviewSkeleton key={i} />
              ))}
            </div>
          ) : (
            reviews.map((review, index) => (
              <ReviewItem
                // Keyed per filter so like state never leaks from a review at
                // the same position in a different filtered list.
                key={`${productId ?? "page"}-${topic ?? "all"}-${index}`}
                review={review}
                surface="sheet"
                index={index}
                topic={topic}
                highlightQuote={review.highlight_quote}
                last={index === reviews.length - 1}
              />
            ))
          )}

          {/* Infinite-scroll sentinel: an invisible probe that triggers the
              next page as it scrolls into view. Only rendered while more pages
              remain, so the last page observes nothing. */}
          {hasNextPage ? (
            <div
              ref={sentinelRef}
              className="tw:h-px tw:w-full"
              aria-hidden="true"
            />
          ) : null}

          {/* A page failed. Auto-loading is stood down (see the sentinel above),
              so this is the only way forward — and it tells the reader there is
              more, rather than letting the list look finished. */}
          {isFetchNextPageError && !isFetchingNextPage ? (
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:py-4">
              <p className="tw:text-sm tw:text-gray-500 tw:m-0">
                Couldn&apos;t load more reviews.
              </p>
              <button
                type="button"
                onClick={() => fetchNextPage()}
                className="tw:text-sm tw:font-medium tw:text-[#523c33] tw:underline tw:bg-transparent tw:border-none tw:cursor-pointer"
              >
                Try again
              </button>
            </div>
          ) : null}

          {isFetchingNextPage ? (
            <div className="tw:flex tw:justify-center tw:py-4">
              <div
                className="tw:h-5 tw:w-5 tw:rounded-full tw:border-2 tw:border-gray-300 tw:border-t-[#523c33] tw:animate-spin"
                role="status"
                aria-label="Loading more reviews"
              />
            </div>
          ) : null}
        </div>

        {SHOW_VERIFIED_FOOTER ? (
          <SheetFooter
            aria-hidden={chromeHidden}
            className={cn(
              "tw:bg-[#4a4a4a] tw:px-4 tw:flex-row tw:justify-center tw:items-center tw:overflow-hidden tw:transition-all tw:duration-200",
              chromeHidden
                ? "tw:max-h-0 tw:py-0 tw:opacity-0"
                : "tw:max-h-24 tw:py-[18px] tw:opacity-100",
            )}
          >
            <VerifiedFooter variant="dark" />
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
